import * as React from "react";
import { Document, Page, Text, View, StyleSheet, PDFViewer, Image } from "@react-pdf/renderer";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getDefaultImage, imageType } from "../../../components/shared/Common";
import { QuestionType } from "../../../components/shared/enums";
import { useEffect } from "react";
import { EqEventAnswer, EqEventQuestion, IEqPublicEvent } from "../../../components/shared/interfaces/GetSchoolTrainingInfoDto";
import { IEqEventData } from "../../../components/shared/interfaces/IEqEventDto";

// Define Interfaces
interface IQuestion {
    id: number;
    questionText: string;
    questionType: QuestionType;
}

interface IAnswer {
    questionId: number;
    question: IQuestion;
    answerText?: string;
    selectedOptions?: { option: { optionText: string } }[];
}

interface IUser {
    firstName?: string;
    lastName?: string;
    userName?: string;
    email?: string;
    phoneNumber?: string;
    userImage?: { url?: string };
}

interface IClassOrder {
    user?: IUser;
    attendUsingName?: string;
    answers?: IAnswer[];
}

interface IClass {
    title: string;
    classOrders: IClassOrder[];
}

interface IAlternative {
    customClassName: string;
    classList: IClass[];
    questions: IQuestion[];
}

interface IEqPublicEventPdfGenerate {
    eventData?: IEqPublicEvent | null;
    selectedDates: string[];
}

// Styles
const styles = StyleSheet.create({
    page: { flexDirection: "column", padding: 30 },
    heading: { fontSize: 20, marginBottom: 5 },
    subHeading: { fontSize: 12, marginBottom: 10 },
    section: { marginBottom: 20, textAlign: "left" },
    alternativeContainer: { border: "1px solid #FF8838", marginBottom: 10, padding: 5 },
    tableContainer: { border: "1px solid #f0f0f0", marginBottom: 10, padding: 10 },
    tableHeader: { flexDirection: "row", backgroundColor: "#ff883873", padding: 5, fontSize: 13, fontWeight: "bold", color: "#ff8838" },
    tableRow: { flexDirection: "row", borderBottom: "1px solid #d9d9d9", paddingVertical: 8 },
    tableHeaderCell: { flex: 1, textAlign: "left", fontSize: 15, fontWeight: 600 },
    tableCellFirst: { flex: 1, textAlign: "left", flexDirection: "row", alignItems: "center", fontSize: 13, fontWeight: 400 },
    avatar: { border: "1px solid #f0f0f0", width: 30, height: 30, marginRight: 5, borderRadius: "50%" },
    tableCell: { flex: 1, textAlign: "left", fontSize: 13, fontWeight: 400, justifyContent: "center" },
    row: { flexDirection: "row", justifyContent: "space-between" },
    sectionTitle: { fontSize: 16, fontWeight: "bold", marginVertical: 5 },
});

// Component
const EqPublicEventPdfGenerate = ({ eventData, selectedDates }: IEqPublicEventPdfGenerate) => {
    const { t } = useTranslation();
    const options: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" };


    useEffect(() => {}, [eventData]);

    if (!eventData) {
        return <div>No event data available.</div>;
    }

    const getTotalAttendees = (): number => {
        return eventData?.alternatives?.reduce((count, alt) => {
            return count + alt.classList.reduce((sum, classItem) => sum + classItem.classOrders.length, 0);
        }, 0) || 0;
    };

    const renderAnswers = (questions: EqEventQuestion[], answers?: EqEventAnswer[]) => (
        <>
            {questions.map((question, index) => {
                const correspondingAnswer = answers?.find(answer => answer.questionId === question.id);
                return (
                    <View key={index} style={styles.tableCell}>
                        <Text>
                            {correspondingAnswer?.question?.questionType === QuestionType.Text ? (
                                <Text>{correspondingAnswer?.answerText}</Text>
                            ) : (
                                correspondingAnswer?.selectedOptions?.map((option, oIdx) => (
                                    <Text key={oIdx}>{option?.option?.optionText}</Text>
                                ))
                            )}
                        </Text>
                    </View>
                );
            })}
        </>
    );

    return (
        <PDFViewer style={{ width: "100%", height: "100vh" }}>
            <Document>
                <Page style={styles.page}>
                    <Text style={styles.heading}>{eventData.title}</Text>
                    <Text style={styles.subHeading}>
                        {" (" +
                            (eventData.startDate
                                ? new Date(eventData.startDate).toLocaleDateString(t("dateLang"), options )
                                : "N/A") +
                            ")"}
                    </Text>
                    <View style={styles.row}>
                        <Text style={styles.subHeading}>
                            {t("starting")}: {moment(eventData.startDate).format("hh:mm")}
                        </Text>
                        <Text style={styles.subHeading}>
                            {t("totalNumberOfAttendees")}: {getTotalAttendees()}
                        </Text>
                    </View>
                    {eventData.alternatives.map((alternative, altIndex) => (
                        <View key={altIndex} style={styles.alternativeContainer}>
                            <Text style={styles.sectionTitle}>{alternative.customClassName}</Text>

                            {alternative.classList.map((classItem, classIdx) => (
                                <View key={classIdx} style={styles.tableContainer}>
                                    <Text style={styles.sectionTitle}>{classItem.title}</Text>

                                    {/* Table Header */}
                                    <View style={styles.tableHeader}>
                                        <View style={styles.tableHeaderCell}>
                                            <Text>{t("attendees")}</Text>
                                        </View>
                                        {/* <View style={styles.tableHeaderCell}>
                                            <Text>{t("phoneNumber")}</Text>
                                        </View> */}
                                        {alternative.questions.map((question, index) => (
                                            <View key={index} style={styles.tableHeaderCell}>
                                                <Text>{question.questionText}</Text>
                                            </View>
                                        ))}
                                    </View>
                                    {/* Table Rows */}
                                    {classItem.classOrders.map((classOrder, orderIdx) => (
                                        <View key={orderIdx} style={styles.tableRow}>
                                            {/* User Avatar and Name */}
                                            <View style={styles.tableCellFirst}>
                                                <Image
                                                    src={classOrder.user?.userImage?.url || getDefaultImage(imageType.partner)}
                                                    style={styles.avatar}
                                                />
                                                <Text>
                                                    {classOrder.attendUsingName || classOrder.user?.firstName || classOrder.user?.userName}
                                                </Text>
                                            </View>
                                            {/* <View style={styles.tableCellFirst}>
                                                <Text>{classOrder.user?.phoneNumber}</Text>
                                            </View> */}
                                            {renderAnswers(alternative.questions, classOrder.answers)}
                                        </View>
                                    ))}
                                </View>
                            ))}
                        </View>
                    ))}
                </Page>
            </Document>
        </PDFViewer>
    );
};

export default EqPublicEventPdfGenerate;
